import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Droplets, ScanSearch, FileText, Calculator, CalendarCheck, Wind, TreePine,
  Waves, Star, Quote, MapPin, CheckCircle2, ClipboardList,
  Building2, ShieldAlert, CalendarClock, FileCheck2, MessagesSquare, PoundSterling,
} from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { Img } from "@/components/site/Img";
import { useSite } from "@/lib/SiteContext";
import {
  Container, Section, SectionHeading, Eyebrow, PageMeta, StatusPill,
} from "@/components/site/Bits";
import { CredentialsBand } from "@/components/site/CredentialsBand";
import { QuickEnquiry } from "@/components/site/QuickEnquiry";
import { PaymentPlanPromo } from "@/components/site/PaymentPlanPromo";
import FinalCTA from "@/components/site/FinalCTA";
import GoogleReviews from "@/components/site/GoogleReviews";
import { TrustBadges } from "@/components/site/TrustBadges";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// Our own photograph: a technician treating a mould-affected wall.
const HERO_IMAGE = "/home-hero.webp";

const TOOLS = [
  {
    icon: ScanSearch,
    heading: "Not sure what is causing the problem?",
    name: "Check My Damp",
    text: "Answer a few simple questions, upload photographs and receive guidance on the most appropriate next inspection.",
    to: "/tools/check-my-damp",
    testId: "tool-card-check-my-damp",
  },
  {
    icon: FileText,
    heading: "Already have a survey report?",
    name: "Upload My Survey",
    text: "Upload an existing property survey, damp report or quotation and receive a clearer explanation of the findings and practical next steps.",
    to: "/tools/upload-my-survey",
    testId: "tool-card-upload-my-survey",
  },
  {
    icon: Calculator,
    heading: "Want the work priced properly?",
    name: "Request an Estimate",
    text: "Tell us what you need, add your measurements and photographs, and our team will prepare a written estimate for you.",
    to: "/tools/get-an-estimate",
    testId: "tool-card-get-an-estimate",
  },
];

const TRUST_DETAIL = {
  "Evidence-led investigations": "Readings and photographs, never guesswork.",
  "Clear recommendations": "Plain English on what genuinely needs doing.",
  "Dedicated customer support": "One organised point of contact throughout.",
  "Professionally managed works": "Qualified, carded operatives on every site.",
};

const SERVICE_GROUPS = [
  { icon: Droplets, title: "Damp investigations", text: "Rising damp, penetrating damp and unexplained moisture, investigated before treatment is recommended.", to: "/services/damp-investigations" },
  { icon: Wind, title: "Condensation and mould", text: "Psychrometric assessment of humidity, heating and surface temperatures with practical recommendations.", to: "/services/condensation" },
  { icon: ScanSearch, title: "Mould remediation", text: "Safe removal combined with correcting the moisture conditions that caused the growth.", to: "/services/mould-remediation" },
  { icon: TreePine, title: "Timber preservation", text: "Woodworm, wet rot and dry rot assessed on evidence, with proportionate repairs.", to: "/services/timber-preservation" },
  { icon: Waves, title: "Below-ground damp protection", text: "Damp at or just below external ground level investigated, with localised protection where remediation calls for it.", to: "/services/damp-investigations" },
  { icon: ClipboardList, title: "Specialist surveys", text: "Detailed written damp and timber reports for owners, landlords and managing agents.", to: "/services/specialist-damp-surveys" },
];

export default function Home() {
  const { settings } = useSite();
  const [reviews, setReviews] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data.slice(0, 3))).catch(() => {});
    api.get("/case-studies").then(({ data }) => setCaseStudies(data.slice(0, 3))).catch(() => {});
    api.get("/articles").then(({ data }) => setArticles(data.slice(0, 3))).catch(() => {});
    api.get("/areas-covered").then(({ data }) => setAreas(data.counties)).catch(() => {});
  }, []);

  const home = settings?.homepage || {};
  const trust = home.trust_statements || [
    "Evidence-led investigations", "Clear recommendations", "Dedicated customer support", "Professionally managed works",
  ];  const steps = settings?.process_steps || [];
  const faqs = settings?.faqs || [];

  return (
    <div data-testid="home-page">
      <PageMeta
        title="Damp, mould and timber preservation specialists | Procure Group"
        description="Evidence-led damp investigations, mould remediation, timber preservation and below-ground damp protection across Bedfordshire, Northamptonshire and Cambridgeshire."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white" data-testid="home-hero">
        <Img
          src={HERO_IMAGE}
          alt="A Procure Group technician in branded workwear applying a fungicidal treatment to a mould-affected bedroom wall"
          sizes="100vw"
          priority
          className="absolute inset-0 h-full w-full object-cover object-[22%_top] sm:origin-top-left sm:scale-[1.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/92 to-white/75
                        sm:bg-gradient-to-r sm:from-white sm:from-30% sm:via-white/60 sm:via-50% sm:to-transparent" />
        <Container>
          <div className="pg-fade-up relative max-w-2xl py-16 md:py-24">
            <Eyebrow>{home.hero_label || "Independent Damp-Proofing Specialists"}</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-pg-dark">
              {home.hero_heading || "Damp problems? Get clear answers and the right solution."}
            </h1>
            <p className="mt-6 text-sm md:text-base text-pg-text leading-relaxed">
              {home.hero_text ||
                "From damp investigations and mould remediation to timber preservation and below-ground damp protection, Procure Group helps you understand the problem, identify likely causes and arrange the right remedial works."}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/book-an-inspection"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-pg-primary px-6 py-3.5 text-sm font-semibold text-white hover:bg-pg-dark transition-colors"
              >
                Book an Inspection <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/tools/check-my-damp"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-pg-primary bg-white px-6 py-3.5 text-sm font-semibold text-pg-primary hover:bg-pg-pale transition-colors"
              >
                Check My Damp
              </Link>
            </div>
            <TrustBadges reviewUrl={settings?.google_review_url || ""} variant="split" />
          </div>
        </Container>
      </section>

      <div className="bg-pg-pale pb-10">
        <Container>
          <ul className="-mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="hero-trust-statements">
            {trust.map((item) => (
              <li
                key={item}
                data-testid={`hero-trust-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group relative overflow-hidden rounded-xl border border-pg-border bg-white p-5 shadow-[0_10px_30px_rgba(15,32,58,0.06)] transition-transform duration-300 hover:-translate-y-1 lg:p-6"
              >
                <span aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-[3px] bg-pg-primary transition-colors duration-300 group-hover:bg-pg-bright" />
                <p className="text-sm font-bold leading-snug text-pg-dark">{item}</p>
                {TRUST_DETAIL[item] && (
                  <p className="mt-1.5 text-[13px] leading-snug text-pg-muted">{TRUST_DETAIL[item]}</p>
                )}
              </li>
            ))}
          </ul>
        </Container>
      </div>

      {/* Family business */}
      <Section testId="home-family">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.75fr] lg:items-center">
          <div>
            <Eyebrow>Family run since day one</Eyebrow>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight text-pg-dark"
                data-testid="home-family-title">
              A Family Business That Puts Your Home First
            </h2>
            <div className="mt-5 max-w-2xl space-y-4 text-base leading-relaxed text-pg-muted"
                 data-testid="home-family-copy">
              <p>
                We’re Callum and Cassie, the husband-and-wife team behind Procure Group. Callum
                brings 18 years of property preservation experience to the surveys and specialist
                works, while Cassie looks after customer care and keeps everything organised.
              </p>
              <p>
                For us, running a family business means taking personal responsibility for the
                advice we give, the work we undertake and the way we treat your home. We want you to
                feel comfortable asking questions and confident in the people you’re dealing with.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="home-family-about"
              className="mt-7 inline-flex min-h-[44px] items-center gap-2 rounded-md bg-pg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-pg-dark md:min-h-0 md:py-3"
            >
              Read more about us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <figure data-testid="home-family-photo" className="mx-auto w-full max-w-[420px] lg:mx-0 lg:max-w-none">
            <Img
              src="/team-children.webp"
              alt="Two young children wearing Procure Group branded t-shirts, holding hands at home"
              sizes="(max-width: 1024px) 90vw, 560px"
              className="w-full rounded-xl border border-pg-border object-cover shadow-sm"
            />
            <figcaption className="mt-3 text-[13px] text-pg-muted sm:text-sm">
              Our smallest team members. Our biggest motivation.
            </figcaption>
          </figure>
        </div>
      </Section>

      <CredentialsBand />

      {/* Online tools */}
      <Section pale testId="home-tools">
        <SectionHeading
          eyebrow="Online assessment tools"
          title="Three practical ways to start"
          intro="Each tool collects your details so we can send you what you have asked for and help you arrange the right next step."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {TOOLS.map((tool, index) => (
            <div
              key={tool.name}
              data-testid={tool.testId}
              className="pg-card pg-card-hover flex flex-col p-7 pg-fade-up"
              style={{ animationDelay: `${index * 90}ms` }}
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-pg-pale text-pg-primary">
                <tool.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-base md:text-lg font-semibold text-pg-dark">{tool.heading}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-pg-muted">{tool.text}</p>
              <Link
                to={tool.to}
                className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-md bg-pg-primary px-4 text-sm font-semibold text-white hover:bg-pg-dark transition-colors md:min-h-0 md:py-2.5"
              >
                {tool.name} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <QuickEnquiry />
        </div>
        <div className="mt-4 rounded-lg border border-pg-border bg-white p-5 text-sm text-pg-muted">
          Prefer to arrange a visit straight away?{" "}
          <Link to="/book-an-inspection" className="pg-link pg-tap font-semibold">
            Book an inspection
          </Link>{" "}
          — a Damp Proofing Quote Assessment is £114 plus VAT, deductible from your final invoice if
          eligible remedial works proceed, subject to the agreed terms.
        </div>
      </Section>

      {/* Services */}
      <Section testId="home-services">        <SectionHeading
          eyebrow="Specialist services"
          title="What we investigate and put right"
          intro="Twenty-four specialist service areas, each explained in plain terms so you can see how the problem is assessed before any work is recommended."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICE_GROUPS.map((service, index) => (
            <Link
              key={service.title}
              to={service.to}
              data-testid={`home-service-${index}`}
              className="pg-card pg-card-hover group p-7"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-pg-pale text-pg-primary">
                <service.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-base md:text-lg font-semibold text-pg-dark">{service.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-pg-muted">{service.text}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-pg-primary">
                Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/services" className="pg-link pg-tap text-sm font-semibold" data-testid="home-all-services">
            View all specialist services →
          </Link>
        </div>
      </Section>

      <Section testId="home-payment-plans" className="!pt-0">
        <PaymentPlanPromo testId="home-payment-plan-promo" />
      </Section>

      {/* Process */}
      <Section pale testId="home-process">
        <SectionHeading
          eyebrow="How the process works"
          title="From first enquiry to aftercare"
          intro="One organised point of contact, with every stage confirmed with you in writing."
        />
        <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="pg-card p-6 pg-fade-up"
              style={{ animationDelay: `${index * 70}ms` }}
              data-testid={`process-step-${index}`}
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-pg-primary text-xs font-bold text-white">
                {index + 1}
              </span>
              <h3 className="mt-4 text-sm md:text-base font-semibold text-pg-dark">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-pg-muted">{step.text}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Why choose */}
      <Section testId="home-why">
        <div className="max-w-3xl">
          <div>
            <SectionHeading
              eyebrow="Why choose Procure Group"
              title="Investigation first, treatment second"
              intro="We investigate the likely cause before recommending treatment, and we avoid unnecessary or unsupported remedial work."
            />
            <ul className="mt-8 space-y-4">
              {[
                "We explain technical issues clearly and in plain terms.",
                "We help you understand existing survey reports and quotations.",
                "We provide clear quotations separating necessary, advisory and optional items.",
                "We keep you informed throughout, with one organised point of contact.",
                "We manage work from initial enquiry through to completion and aftercare.",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm md:text-base text-pg-text leading-relaxed">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-pg-primary" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-8 rounded-lg border border-pg-border bg-pg-pale p-5 text-sm leading-relaxed text-pg-muted">
              Evidence-led property investigations and proportionate remedial recommendations. Where we
              may also carry out the works we recommend, we say so plainly so you can weigh our advice
              accordingly.
            </p>
          </div>
        </div>
      </Section>

      <GoogleReviews reviews={reviews} settings={settings} />

      {/* Case studies */}
      <Section testId="home-case-studies">
        <SectionHeading
          eyebrow="Project case studies"
          title="Completed work in our area"
          intro="Recent projects across the counties we cover, photographed on site as the work was completed."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {caseStudies.map((study) => (
            <article key={study.id} className="pg-card pg-card-hover overflow-hidden" data-testid={`case-study-${study.slug}`}>
              {study.image_after && (
                <Img
                  src={study.image_after}
                  alt={study.title}
                  sizes="(max-width: 768px) 100vw, 360px"
                  className="h-44 w-full object-cover"
                />
              )}
              <div className="p-6">
                <StatusPill>{study.service}</StatusPill>
                <h3 className="mt-3 text-base font-semibold text-pg-dark">{study.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-pg-muted line-clamp-3">
                  {study.solution || study.problem}
                </p>
                <p className="mt-3 text-xs text-pg-muted">
                  {[study.property_type, study.location].filter(Boolean).join(" · ")}
                </p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* Articles */}
      <Section pale testId="home-articles">
        <SectionHeading
          eyebrow="Advice centre"
          title="Understand the problem before you spend"
          intro="Practical, plainly written guidance on damp, condensation, timber and below-ground damp protection."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/advice/${article.slug}`}
              className="pg-card pg-card-hover p-7"
              data-testid={`home-article-${article.slug}`}
            >
              <StatusPill>{article.category}</StatusPill>
              <h3 className="mt-3 text-base font-semibold text-pg-dark">{article.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-pg-muted">{article.standfirst}</p>
              <span className="mt-4 inline-block text-sm font-semibold text-pg-primary">Read article →</span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/advice" className="pg-link pg-tap text-sm font-semibold">
            Browse all advice articles →
          </Link>
        </div>
      </Section>

      {/* Areas */}
      <Section testId="home-areas">
        <SectionHeading
          eyebrow="Areas covered"
          title="Where we work"
          intro="We work across Bedfordshire, Cambridgeshire, Hertfordshire, Milton Keynes, Northamptonshire and Peterborough."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {areas.map((area) => (
            <Link
              key={area.slug}
              to={`/areas-covered/${area.slug}`}
              className="pg-card pg-card-hover flex items-center gap-3 p-5"
              data-testid={`home-area-${area.slug}`}
            >
              <MapPin className="h-4 w-4 shrink-0 text-pg-primary" />
              <span className="text-sm font-semibold text-pg-dark">{area.name}</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* FAQs */}
      <Section pale testId="home-faqs">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="Frequently asked questions" title="Common questions, answered plainly" />
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="border-pg-border">
                <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-pg-dark hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-pg-muted">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      <FinalCTA />
    </div>
  );
}
